import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, useMotionValue, useTransform, PanInfo, animate } from 'framer-motion';

import ZoomInIcon from './icons/ZoomInIcon';
import ZoomOutIcon from './icons/ZoomOutIcon';
import RefreshCwIcon from './icons/RefreshCwIcon';

interface ImageInImageViewerProps {
  beforeImageUrl: string;
  afterImageUrl: string;
  aspectRatio: number;
}

const ImageInImageViewer: React.FC<ImageInImageViewerProps> = ({ beforeImageUrl, afterImageUrl, aspectRatio }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const pipRef = useRef<HTMLDivElement>(null);
    
    // PiP window state
    const width = useMotionValue(350);
    const height = useTransform(width, w => w / aspectRatio);
    
    // Inner PiP image state
    const imageScale = useMotionValue(1);
    const imageX = useMotionValue(0);
    const imageY = useMotionValue(0);
    const [imageDragConstraints, setImageDragConstraints] = useState({ top: 0, left: 0, right: 0, bottom: 0 });
    
    // Background image state
    const [backgroundState, setBackgroundState] = useState({ scale: 1, x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const panStartPoint = useRef({ x: 0, y: 0 });

    // --- PiP LOGIC ---
    const handleResize = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
        const newWidth = Math.max(150, Math.min(width.get() + info.delta.x * 2, containerRef.current?.clientWidth || 800));
        width.set(newWidth);
    };

    const handlePipWheel = (e: React.WheelEvent) => {
        e.stopPropagation();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(1, Math.min(imageScale.get() * delta, 5));
        
        if (pipRef.current) {
            const rect = pipRef.current.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            
            const oldImageScale = imageScale.get();
            const worldX = (mouseX - imageX.get()) / oldImageScale;
            const worldY = (mouseY - imageY.get()) / oldImageScale;
            
            const newImageX = mouseX - worldX * newScale;
            const newImageY = mouseY - worldY * newScale;
            
            imageScale.set(newScale);
            imageX.set(newImageX);
            imageY.set(newImageY);
        }
    };

    useEffect(() => {
        const updateConstraints = () => {
            const scale = imageScale.get();
            const w = width.get();
            const h = height.get();
            const extraWidth = (w * scale - w) / 2;
            const extraHeight = (h * scale - h) / 2;

            setImageDragConstraints({
                left: -extraWidth,
                right: extraWidth,
                top: -extraHeight,
                bottom: extraHeight,
            });
            
            imageX.set(Math.max(-extraWidth, Math.min(imageX.get(), extraWidth)));
            imageY.set(Math.max(-extraHeight, Math.min(imageY.get(), extraHeight)));
        };

        const unsubscribeScale = imageScale.on("change", updateConstraints);
        const unsubscribeWidth = width.on("change", updateConstraints);

        updateConstraints();
        
        return () => {
            unsubscribeScale();
            unsubscribeWidth();
        };
    }, [imageScale, width, height, imageX, imageY]);
    
    const resetPipImageTransform = () => {
        animate(imageScale, 1);
        animate(imageX, 0);
        animate(imageY, 0);
    };

    // --- BACKGROUND LOGIC ---
    const handleBackgroundZoom = useCallback((factor: number, centerX?: number, centerY?: number) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = centerX ?? rect.left + rect.width / 2;
        const mouseY = centerY ?? rect.top + rect.height / 2;

        setBackgroundState(prev => {
            const newScale = Math.max(1, Math.min(prev.scale * factor, 5));
            if (newScale === prev.scale) return prev;

            const worldX = (mouseX - rect.left - prev.x) / prev.scale;
            const worldY = (mouseY - rect.top - prev.y) / prev.scale;

            let newX = mouseX - rect.left - worldX * newScale;
            let newY = mouseY - rect.top - worldY * newScale;

            const newWidth = rect.width * newScale;
            const newHeight = rect.height * newScale;
            const maxX = (newWidth - rect.width) / 2;
            const maxY = (newHeight - rect.height) / 2;
            
            newX = Math.max(-maxX, Math.min(newX, maxX));
            newY = Math.max(-maxY, Math.min(newY, maxY));
            
            if (newScale === 1) {
              return { scale: 1, x: 0, y: 0 };
            }

            return { scale: newScale, x: newX, y: newY };
        });
    }, []);

    const handleContainerWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        handleBackgroundZoom(e.deltaY > 0 ? 0.9 : 1.1, e.clientX, e.clientY);
    };

    const handlePanStart = (e: React.MouseEvent) => {
        if (backgroundState.scale <= 1) return;
        e.preventDefault();
        setIsPanning(true);
        panStartPoint.current = { x: e.clientX - backgroundState.x, y: e.clientY - backgroundState.y };
    };

    const handlePanMove = (e: React.MouseEvent) => {
        if (!isPanning || backgroundState.scale <= 1) return;
        
        let newX = e.clientX - panStartPoint.current.x;
        let newY = e.clientY - panStartPoint.current.y;
        
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const newWidth = rect.width * backgroundState.scale;
            const newHeight = rect.height * backgroundState.scale;
            const maxX = (newWidth - rect.width) / 2;
            const maxY = (newHeight - rect.height) / 2;
            
            newX = Math.max(-maxX, Math.min(newX, maxX));
            newY = Math.max(-maxY, Math.min(newY, maxY));
        }

        setBackgroundState(prev => ({ ...prev, x: newX, y: newY }));
    };

    const handlePanEnd = () => {
        setIsPanning(false);
    };

    const resetBackground = () => {
        setBackgroundState({ scale: 1, x: 0, y: 0 });
    };

    const containerCursor = backgroundState.scale > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default';

    return (
        <div 
            ref={containerRef} 
            className="relative w-full h-full select-none overflow-hidden rounded-2xl shadow-2xl shadow-black/50 bg-black"
            onWheel={handleContainerWheel}
            onMouseDown={handlePanStart}
            onMouseMove={handlePanMove}
            onMouseUp={handlePanEnd}
            onMouseLeave={handlePanEnd}
            style={{ cursor: containerCursor }}
        >
            <motion.div
                className="w-full h-full"
                animate={{
                    scale: backgroundState.scale,
                    x: backgroundState.x,
                    y: backgroundState.y
                }}
                transition={{ type: 'tween', duration: 0.1, ease: 'linear' }}
            >
                <img src={afterImageUrl} alt="After" className="w-full h-full object-contain" draggable="false" />
            </motion.div>
            
            <motion.div
                ref={pipRef}
                drag
                dragConstraints={containerRef}
                dragMomentum={false}
                style={{ width, height, cursor: 'grab' }}
                className="absolute top-8 left-8 rounded-xl shadow-2xl overflow-hidden border-2 border-white/30 focus-within:border-teal-500 focus-within:ring-2 focus-within:ring-teal-500"
                whileDrag={{ cursor: 'grabbing' }}
                onWheel={handlePipWheel}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <motion.div
                    drag={imageScale.get() > 1}
                    dragConstraints={imageDragConstraints}
                    dragMomentum={false}
                    style={{ 
                        x: imageX, y: imageY, scale: imageScale,
                        backgroundImage: `url(${beforeImageUrl})`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        cursor: imageScale.get() > 1 ? 'grab' : 'default',
                    }}
                    whileDrag={{ cursor: 'grabbing' }}
                    className="w-full h-full"
                />

                <div className="absolute top-1 left-1 flex items-center gap-1 p-1 rounded-full bg-black/40 backdrop-blur-sm">
                    <button onClick={() => imageScale.set(Math.min(5, imageScale.get() * 1.2))} className="p-1 text-white hover:bg-white/20 rounded-full"><ZoomInIcon className="w-4 h-4"/></button>
                    <button onClick={() => imageScale.set(Math.max(1, imageScale.get() / 1.2))} className="p-1 text-white hover:bg-white/20 rounded-full"><ZoomOutIcon className="w-4 h-4"/></button>
                    <button onClick={resetPipImageTransform} className="p-1 text-white hover:bg-white/20 rounded-full"><RefreshCwIcon className="w-4 h-4"/></button>
                </div>

                <motion.div
                    onPan={handleResize}
                    onPanStart={(e) => e.stopPropagation()}
                    className="absolute -bottom-1.5 -right-1.5 w-5 h-5 cursor-se-resize flex items-center justify-center"
                >
                    <div className="w-3 h-3 rounded-full bg-white/50 border border-black/20"/>
                </motion.div>
            </motion.div>

            <div className="absolute bottom-4 right-4 z-20 flex items-center gap-1 p-1.5 rounded-full bg-zinc-950/60 backdrop-blur-xl border border-white/10 shadow-lg">
                <button onClick={() => handleBackgroundZoom(1.2)} title="Zoom In Background" className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 transition-colors"><ZoomInIcon className="w-5 h-5"/></button>
                <button onClick={() => handleBackgroundZoom(0.8)} title="Zoom Out Background" className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 transition-colors" disabled={backgroundState.scale <= 1}><ZoomOutIcon className="w-5 h-5"/></button>
                <button onClick={resetBackground} title="Reset Background View" className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 transition-colors"><RefreshCwIcon className="w-5 h-5"/></button>
            </div>
        </div>
    );
};

export default ImageInImageViewer;
